import React, { useState } from "react";
import cn from "classnames";
import { noop } from "lib/utils";
import useLayout from "hooks/useLayout";
import useModal from "hooks/useModal";
import useOuterClick from "hooks/useOuterClick";
import A11yElement from "components/helpers/A11yElement";
import { countRailcards } from "lib/utils";
import ModalPassengersForm from "components/ModalPassengersForm";
import ModalPassengersSeasonForm from "components/ModalPassengersSeasonForm";
import PassengersForm from "components/PassengersForm";
import PassengersSeasonForm from "components/PassengersSeasonForm";
import { ReactComponent as PassengerIcon } from "static/images/passenger.svg";
import { ReactComponent as PassengerMultiIcon } from "static/images/passenger-multi.svg";
import "styles/components/passengers.scss";
import { trackPassengerModal } from "lib/tracking";
import { useGTMChange } from "hooks/useGTM";

const Passengers = ({
  adults = 1,
  children = 0,
  errorRailcards,
  hasRailcard,
  hasJCPRailcard,
  isSeason = false,
  onChange = noop,
  passengerType,
  railcards = [],
  disableRailcards = false,
  disableInput = false
}) => {
  const { isMobile } = useLayout();
  const [isOpen, setOpen] = useState(false);
  const { closeModal, showModal } = useModal();
  const refOuterClick = useOuterClick(e => {
    if (!e.target?.className?.includes?.("icon")) {
      setOpen(false);
    }
  });

  useGTMChange(isOpen, trackPassengerModal);

  const handleCloseModal = () => {
    setOpen(false);
  };

  const onHide = () => {
    setOpen(false);
  };

  const onSavePassengers = ({ adults, children, railcards }) => {
    onAdultsChange(adults);
    onChildrenChange(children);
    onRailcardsChange(railcards);
  };

  const onSavePassengersSeason = ({ hasRailcard, hasJCPRailcard, passengerType }) => {
    onChange("hasRailcard")(hasRailcard);
    onChange("hasJCPRailcard")(hasJCPRailcard);
    onChange("passengerType")(passengerType);
  };

  const onClick = () => {
    setOpen(!isOpen);
    if (isMobile) {
      if (isSeason) {
        showModal(
          <ModalPassengersSeasonForm
            hasRailcard={hasRailcard}
            hasJCPRailcard={hasJCPRailcard}
            onClose={closeModal}
            passengerType={passengerType}
            onSave={onSavePassengersSeason}
          />,
          { onClose: handleCloseModal }
        );
      } else {
        showModal(
          <ModalPassengersForm
            adults={adults}
            children={children}
            passengers={adults + children}
            railcards={railcards}
            onClose={closeModal}
            onSave={onSavePassengers}
            disableRailcards={disableRailcards}
          />,
          { onClose: handleCloseModal }
        );
      }
    }
  };

  const onAdultsChange = adults => {
    onChange("adults")(adults);
  };

  const onChildrenChange = children => {
    onChange("children")(children);
  };

  const onRailcardsChange = railcards => {
    onChange("railcards")(railcards);
  };

  const numRailcards = countRailcards(railcards);

  const PassengerCount = () => {
    if (isSeason) {
      return <>{passengerType === "adult" ? "Adult" : "Child"}</>;
    } else {
      return (
        <>
          <span>
            {adults > 0 ? `${adults} Adult${adults > 1 ? "s" : ""}` : ""}
          </span>
          <span>
            {children > 0 ? `${children} Child${children > 1 ? "ren" : ""}` : ""}
          </span>
          {adults === 0 && children === 0 && <span>Please select</span>}
        </>
      );
    }
  };

  const RailcardsCount = () => {
    if (isSeason) {
      if (hasRailcard & !hasJCPRailcard) {
        return <>16/17 Railcard</>;
      } else if (!hasRailcard && hasJCPRailcard) {
        return <>Jobcentre Plus railcard</>;
      } else if (hasRailcard && hasJCPRailcard) {
        return <>16/17 Railcard, Jobcentre Plus railcard</>;
      } else {
        return <>No railcards</>;
      }
    } else {
      return (
        <>
          {numRailcards ? `${numRailcards} railcard${numRailcards > 1 ? "s" : ""}` : "No railcards"}
        </>
      );
    }
  };

  return (
    <div
      className={cn("otrl-jp__passengers-container", {
        "otrl-jp__passengers-container--disabled": disableInput
      })}
      ref={refOuterClick}
    >
      <A11yElement
        role="button"
        className={cn("otrl-jp__passengers", {
          "otrl-jp__passengers--opened": isOpen
        })}
        onAction={onClick}
        tabIndex={disableInput ? "-1" : "0"}
      >
        <div className="otrl-jp__passengers__button">
          {adults + children > 1 ? (
            <PassengerMultiIcon className="otrl-jp__passengers__multi-icon" />
          ) : (
            <PassengerIcon className="otrl-jp__passengers__icon" />
          )}
          <div className="otrl-jp__passengers__group">
            <span className="otrl-jp__passengers__type">
              {isMobile ? "Passengers" : <PassengerCount />}
            </span>
            <div className="otrl-jp__passengers__second-row">
              <span className="otrl-jp__passengers__railcards">
                {isMobile ? <PassengerCount /> : !disableRailcards && <RailcardsCount />}
              </span>
              {isMobile && !disableRailcards && <RailcardsCount />}
            </div>
          </div>
        </div>
      </A11yElement>

      <div className="otrl-jp__passengers__dropdown">
        {!isMobile
          ? isOpen &&
            (isSeason ? (
              <PassengersSeasonForm
                hasRailcard={hasRailcard}
                hasJCPRailcard={hasJCPRailcard}
                onChange={onChange}
                passengerType={passengerType}
              />
            ) : (
              <PassengersForm
                adults={adults}
                children={children}
                onAdultsChange={onAdultsChange}
                onChildrenChange={onChildrenChange}
                onHide={onHide}
                onRailcardsChange={onRailcardsChange}
                passengers={adults + children}
                railcards={railcards}
                disableRailcards={disableRailcards}
              />
            ))
          : null}

        {errorRailcards && (
          <span className="otrl-jp__passengers__railcards__error">
            You have selected too many railcards
          </span>
        )}
      </div>
    </div>
  );
};

export default Passengers;
